import { FieldList, PlanBadge, Radio, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import type { TargetingRule } from '@hotjar/feedback/domain/common/TargetingRule/TargetingRule';
import { RULE_TRIGGER_TYPE } from '@hotjar/feedback/domain/common/TargetingRule/TargetingRule';
import { v4 as uuid } from 'uuid';
import {
  UserAttributeDtoKind,
  UserAttributeKindByStringDtoValue,
  UserAttributeToDto,
} from '@hotjar/feedback/domain/common/UserAttributes/UserAttributesMapping';
import type { UserAttributeType } from '@hotjar/global-filters';
import type { UserAttributeDto } from '@hotjar/feedback/domain/common/UserAttributes/UserAttributeDto';
import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { usePlanForFeature } from '@hotjar/webapp/hooks/usePlanForFeature';
import { useAskFeature } from '@hotjar/feedback';
import { useTranslation, useTranslationWithPrefix } from '@hotjar/translations';
import { UserAttributesTargetingRule } from './UserAttributesTargetingRule';
import { UserAttributesChooser } from './UserAttributesChooser';

const StyledRadio = styled(Radio)`
  margin-bottom: 0 !important;
`;

const StyledFieldList = styled(FieldList)`
  margin-top: ${tokens.spacer.x3};
  margin-bottom: ${tokens.spacer.x2};
  width: auto;
`;

const TargetingSeparator = styled.div`
  ${tokens.text('caption')}
  color: rgba(0,0,0,0.6);
`;

const SpecificUsers = styled.div`
  margin-left: ${tokens.spacer.x2};
  width: auto;
`;

const SelectedUserAttributes = styled.div`
  margin-top: ${tokens.spacer.x1};
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin-left: ${tokens.spacer.x2};
`;

export enum TARGETING_MODE {
  ALL = 'all',
  SPECIFIC = 'specific',
}

const FEATURE_NAME = 'user_attributes.toggle';

export interface UserAttributesTargetingProps {
  targetingMode: TARGETING_MODE;
  onTargetingModeChange: (newValue: TARGETING_MODE) => void;
  onAddRule: (newRule: TargetingRule) => void;
  onRemoveRule: (rule: TargetingRule) => void;
  onChangeRule: (rule: TargetingRule) => void;
  targetingRules: TargetingRule[] | undefined;
  userAttributes: UserAttributeDto[];
  onShowUpsellModal: () => void;
  entityType: 'survey' | 'widget' | 'invitation';
}

export function UserAttributesTargeting({
  targetingMode,
  onTargetingModeChange,
  targetingRules,
  onRemoveRule,
  onChangeRule,
  onAddRule,
  userAttributes,
  onShowUpsellModal,
  entityType,
}: UserAttributesTargetingProps): JSX.Element {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.targeting.user-attribute');
  const { t: commonT } = useTranslation('common');
  const userAttributeOptionMap = useMemo(() => {
    return new Map(
      userAttributes
        .filter((item) => item.rule_type !== UserAttributeDtoKind.UNKNOWN)
        .map((item) => {
          return [
            item.name,
            { kind: UserAttributeKindByStringDtoValue[item.rule_type], name: item.name },
          ];
        }),
    );
  }, [userAttributes]);

  const userTargetingRules = useMemo(() => {
    if (targetingRules === undefined) return undefined;
    const rules = targetingRules.filter((rule) => rule.component === RULE_TRIGGER_TYPE.ATTRIBUTE);
    rules.forEach((rule) => {
      if (!rule.uiUuid) {
        rule.uiUuid = uuid();
      }
    });
    return rules;
  }, [targetingRules]);

  const { data: site = {} as Site } = useCurrentSite();
  const canUseUserTargeting = useAskFeature(FEATURE_NAME, site);
  const planNeededForUserTargeting = usePlanForFeature(FEATURE_NAME);

  const handleUserAttributeSelected = (newValue: UserAttributeType) => {
    onAddRule({
      negate: false,
      // eslint-disable-next-line camelcase
      rule_type: UserAttributeToDto[newValue.kind],
      name: newValue.name,
      component: RULE_TRIGGER_TYPE.ATTRIBUTE,
      uiUuid: uuid(),
    });
  };

  return (
    <>
      <StyledFieldList title={t('title')} helperText={t('description', { entityType })} size="m">
        <StyledRadio
          isChecked={targetingMode === TARGETING_MODE.ALL}
          id={`user_targeting_${TARGETING_MODE.ALL}`}
          name={`USER_TARGETING_${TARGETING_MODE.ALL}`}
          onChange={() => {
            onTargetingModeChange(TARGETING_MODE.ALL);
          }}
          value={TARGETING_MODE.ALL}
          labelContent={t('options.all')}
        />
        <StyledRadio
          isChecked={targetingMode === TARGETING_MODE.SPECIFIC}
          id={`user_targeting_${TARGETING_MODE.SPECIFIC}`}
          name={`USER_TARGETING_${TARGETING_MODE.SPECIFIC}`}
          onChange={() => {
            if (canUseUserTargeting) {
              onTargetingModeChange(TARGETING_MODE.SPECIFIC);
            } else {
              onShowUpsellModal();
            }
          }}
          value={TARGETING_MODE.SPECIFIC}
          labelContent={
            <>
              {t('options.specific.title')}
              {!canUseUserTargeting && planNeededForUserTargeting && (
                <StyledPlanBadge kind={planNeededForUserTargeting} size="s" />
              )}
            </>
          }
        />
      </StyledFieldList>
      {targetingMode === TARGETING_MODE.SPECIFIC && (
        <SpecificUsers>
          <StyledFieldList title={t('options.specific.description', { entityType })}>
            <div>
              <UserAttributesChooser
                onUserAttributeSelected={(a) => handleUserAttributeSelected(a)}
                userAttributeOptionIsLoading={false}
                userAttributeOptionList={Array.from(userAttributeOptionMap.values())}
              />
              {userTargetingRules !== undefined && userTargetingRules.length > 0 && (
                <SelectedUserAttributes>
                  {userTargetingRules.map((rule, index) => {
                    return (
                      <>
                        {index !== 0 && (
                          <TargetingSeparator>
                            {commonT('and').toLocaleUpperCase()}
                          </TargetingSeparator>
                        )}
                        <UserAttributesTargetingRule
                          key={rule.uiUuid}
                          rule={rule}
                          onRemoveRule={onRemoveRule}
                          onChangeRule={onChangeRule}
                        />
                      </>
                    );
                  })}
                </SelectedUserAttributes>
              )}
            </div>
          </StyledFieldList>
        </SpecificUsers>
      )}
    </>
  );
}
